import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { syncManager } from '@/offline/syncManager';
import { mergeLocalConcepts } from '@/offline/concepts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AppContext = createContext(null);

// Selección persistida para que una recarga sin conexión no pierda contrato/concepto
const readStored = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeStored = (key, value) => {
  if (value) localStorage.setItem(key, JSON.stringify(value));
  else localStorage.removeItem(key);
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProjectState] = useState(() => readStored('generia_selected_project'));
  const [concepts, setConcepts] = useState([]);
  const [selectedConcept, setSelectedConceptState] = useState(() => readStored('generia_selected_concept'));
  const [captureDestino, setCaptureDestinoState] = useState(() => readStored('generia_capture_destino'));
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [loading, setLoading] = useState(true);

  const setSelectedProject = (project) => {
    writeStored('generia_selected_project', project);
    setSelectedProjectState(project);
  };
  const setSelectedConcept = (concept) => {
    writeStored('generia_selected_concept', concept);
    setSelectedConceptState(concept);
  };
  const setCaptureDestino = (destino) => {
    writeStored('generia_capture_destino', destino);
    setCaptureDestinoState(destino);
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      fetchProjects();
    } else {
      setProjects([]);
      setSelectedProject(null);
      setConcepts([]);
      setSelectedConcept(null);
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (selectedProject) {
      fetchConcepts(selectedProject.id);
    } else {
      setConcepts([]);
      setSelectedConcept(null);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects`, {
        params: {
          user_id: user.id,
          role: user.role,
        },
      });
      setProjects(response.data);
      // DO NOT auto-select - let user choose from ContratosPage
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConcepts = async (projectId) => {
    let serverConcepts = [];
    try {
      const response = await axios.get(`${API}/concepts`, {
        params: { project_id: projectId },
      });
      serverConcepts = response.data;
      // DO NOT auto-select - let user choose from CatalogoPage
    } catch (error) {
      console.error('Error fetching concepts:', error);
    }
    setConcepts(await mergeLocalConcepts(projectId, serverConcepts));
  };

  // Refrescar conceptos cuando se agregan localmente o terminan de sincronizar
  useEffect(() => {
    if (!selectedProject) return;
    const handler = () => fetchConcepts(selectedProject.id);
    syncManager.addEventListener('concepts-changed', handler);
    return () => syncManager.removeEventListener('concepts-changed', handler);
  }, [selectedProject]);

  // Clear selections (for logout or navigation)
  const clearSelections = () => {
    setSelectedProject(null);
    setSelectedConcept(null);
    setConcepts([]);
  };

  const value = {
    // Data
    projects,
    concepts,
    // Selection state
    selectedProject,
    setSelectedProject,
    selectedConcept,
    setSelectedConcept,
    captureDestino,
    setCaptureDestino,
    // Status
    isOnline,
    loading,
    // Actions
    refreshProjects: fetchProjects,
    refreshConcepts: () => selectedProject && fetchConcepts(selectedProject.id),
    clearSelections,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContext;
